import { doc, getDoc } from "firebase/firestore";
import { LogOut } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useUser } from "../contexts/UserContext";
import { auth, db } from "../firebaseConfig";
import { VoiceVisualization } from "./VoiceViz";

const Payment = ({ type }) => {
	const { user } = useUser();
	const [loading, setLoading] = useState(true);
	const [hasExistingSubscription, setHasExistingSubscription] =
		useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		const checkExistingSubscription = async () => {
			if (user?.uid) {
				try {
					const userRef = doc(db, "Users", user.uid);
					const userDoc = await getDoc(userRef);

					if (userDoc.exists()) {
						const userData = userDoc.data();
						setHasExistingSubscription(
							!!userData.stripeCustomerId ||
								!!userData.stripeSessionId
						);
					}
				} catch (err) {
					console.error("Error checking subscription:", err);
					setError("Unable to verify subscription status");
				} finally {
					setLoading(false);
				}
			}
		};

		checkExistingSubscription();
	}, [user]);

	const handleManageBilling = async () => {
		try {
			const response = await fetch(
				"/.netlify/functions/create-portal-session",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ userId: user.uid }),
				}
			);

			if (!response.ok)
				throw new Error("Failed to access billing portal");

			const data = await response.json();
			if (data.url) {
				window.location.href = data.url;
			} else {
				throw new Error("No portal URL received");
			}
		} catch (err) {
			console.error("Error accessing billing portal:", err);
			setError(
				"Unable to access billing portal. Please try again later."
			);
		}
	};

	const handleLogout = async () => {
		try {
			await auth.signOut();
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	const getFreeCreditsMessage = () => {
		if (type === "Therapy") {
			return "Subscribe to continue your mental health journey with unlimited therapy sessions and journaling";
		} else if (type === "Journal") {
			return "Subscribe to continue documenting your thoughts and feelings with unlimited journaling and therapy sessions";
		}
		return "You've reached the limit of your free access. Subscribe to continue using all features.";
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
			</div>
		);
	}

	return (
		<div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
			<div className="w-full max-w-md backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden flex flex-col bg-white">
				<div className="p-4">
					<div className="text-center">
						<h1 className="text-2xl font-bold text-gray-800">
							Continue Your Journey
						</h1>
						<p className="text-gray-600 mt-2">
							{user?.displayName || user?.email}
						</p>
						<button
							onClick={handleLogout}
							className="text-sm text-red-600 hover:text-red-800 flex items-center justify-center mx-auto mt-2">
							<LogOut size={16} className="mr-1" />
							Not you? Sign out
						</button>
					</div>

					<div className="flex justify-center mt-0 mb-5">
						<div className="w-1/2">
							<VoiceVisualization isActive={false} />
						</div>
					</div>

					<div className="space-y-4 p-4">
						{error && (
							<div className="text-red-500 text-sm text-center">
								{error}
							</div>
						)}

						{hasExistingSubscription ? (
							<>
								<h2 className="text-xl font-semibold text-gray-800">
									Manage Your Subscription
								</h2>
								<p className="text-gray-600">
									Your subscription needs to be reactivated to
									continue using Milburn.
								</p>
								<button
									onClick={handleManageBilling}
									className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300">
									Manage Subscription
								</button>
							</>
						) : (
							<>
								<h2 className="text-xl font-semibold text-gray-800">
									Your free {type} credits have been exhausted
								</h2>
								<p className="text-gray-600">
									{getFreeCreditsMessage()}
								</p>
								<a
									href={`https://buy.stripe.com/28o4ifbjnd372Dm4gh?client_reference_id=${user.uid}`}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-block w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300 text-center">
									Subscribe Now for less than $10/month <br />
									<span className="text-sm">
										Comes with a one week free trial
									</span>
								</a>
							</>
						)}

						<p className="text-sm text-gray-500 mt-4">
							Your subscription includes{" "}
							<span className="font-bold">unlimited</span> AI
							therapy sessions, journalling sessions, progress
							tracking, reports, and prioritized support.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Payment;
